import { useQuery } from '@tanstack/react-query';
import { useBillingApi } from '../../billing-host/api';
import { BILLING_PERMISSION_CODES } from '../../billing-host/permission-codes';
import { usePermissions } from '../../billing-host/permissions';

/**
 * SFE §2.31 state of the MCF serving the current établissement, as computed
 * by the API from the device itself. `avertissementRequis` already carries
 * the threshold and the hardware scope, so no caller re-derives them.
 */
export interface McfConnexionAlerte {
  /** True when the établissement runs a hardware MCF. */
  machine: boolean;
  /** Days since the MCF last reached the Administration, null when unread. */
  joursDepuisConnexion: number | null;
  seuilJours: number;
  /** True when the MCF's own date shows more than `seuilJours` days. */
  avertissementRequis: boolean;
  /**
   * True when the MCF answers with no date at all. Kept apart from
   * `avertissementRequis`: an unread device is a link problem to report as
   * such, never a statement about when the MCF last reached the
   * Administration.
   */
  lectureIndisponible: boolean;
  /**
   * Why an MCF behind the site agent was not read: `AGENT_DECONNECTE` (no
   * agent connected), `AGENT_NON_VERIFIE` (the server has not verified the
   * agent's state yet), `LECTURE_MCF_ECHOUEE` (the agent could not read the
   * MCF), `SIGNATURE_INTERROMPUE` (the agent holds back its reads while a
   * signature it began has no established outcome) or
   * `AGENT_PROTOCOLE_LEGACY` (the agent runs MCF_PROTOCOL=legacy). Null when
   * no site agent reads the MCF, or when it was read.
   */
  causeLectureIndisponible:
    | 'AGENT_DECONNECTE'
    | 'AGENT_NON_VERIFIE'
    | 'LECTURE_MCF_ECHOUEE'
    | 'SIGNATURE_INTERROMPUE'
    | 'AGENT_PROTOCOLE_LEGACY'
    | null;
  /**
   * With `SIGNATURE_INTERROMPUE` only: true when the agent reads the MCF again
   * after the next invoice it certifies; false for an older agent.
   */
  repriseApresFactureCertifiee?: boolean;
}

/**
 * Annex SECEF BF ST 1.0 status bit 1.4 (daily report due, p. 24), as the MCF
 * reported it on the établissement's latest recorded attempt.
 */
export interface McfRapportQuotidienAlerte {
  /** When that attempt was recorded, ISO 8601. */
  signaleLe: string;
}

interface McfConnexionAlerteResponse {
  /**
   * True when the caller's établissement issues CERTIFIED today, as the
   * API's issuance-regime service decides it. False when the tenant is not
   * subject (category, suspension of the obligation) or the transitional
   * « pas de MCF » mode applies: the API then sends no MCF alert.
   */
  mcfRequis: boolean;
  /** Null when `mcfRequis` is false. */
  alerteConnexion: McfConnexionAlerte | null;
  /** Null or absent when the MCF did not report bit 1.4. */
  alerteRapportQuotidien?: McfRapportQuotidienAlerte | null;
}

export const mcfKeys = {
  alerteConnexion: ['mcf', 'alerte-connexion'] as const,
  status: ['mcf', 'status'] as const,
  signataire: ['mcf', 'signataire'] as const,
  outages: ['mcf', 'pannes'] as const,
};

/**
 * #328 — GET /api/mcf/signataire: the signer of the caller's établissement.
 * `certifie` is decided by the API (`mcfRequis`, the device class and the
 * modality); no screen re-derives it.
 */
export interface McfSignataire {
  /** True when the établissement issues CERTIFIED today. */
  mcfRequis: boolean;
  /**
   * What would sign for it: CERTIFYING (a device speaking the annex
   * protocol), EVALUATION (a device that signs without certifying), NONE
   * (nothing can sign), FROZEN (the attestation of the SFE version this
   * install runs is withdrawn). Null when the tenant is not subject.
   */
  dispositif: 'CERTIFYING' | 'EVALUATION' | 'NONE' | 'FROZEN' | null;
  /** `FISCAL_MODALITIES` code its signer gives its invoices; null when nothing shows it yet. */
  modalite: string | null;
  /** True when a certifying MCF signs its invoices. */
  certifie: boolean;
}

/** GET /api/mcf/status: the site agent's link with this API. */
export interface McfAgentLinkStatus {
  connected: boolean;
  connectedAt?: string;
  /** Set, with `connected` false, while the server has not verified the state of the agent serving this poste. */
  verifying?: true;
  /** True when the établissement signs on the annex simulator: no agent is enrolled for it. */
  simulateur: boolean;
  alerteHorloge: string | null;
}

export function useMcfAgentLinkStatus(options: { refetchIntervalMs?: number } = {}) {
  const api = useBillingApi();
  return useQuery({
    queryKey: mcfKeys.status,
    queryFn: () => api.get<McfAgentLinkStatus>('/mcf/status'),
    refetchInterval: options.refetchIntervalMs ?? false,
    retry: false,
  });
}

/**
 * Polls the §2.31 state hourly: the underlying value moves by the day, and a
 * failure stays silent rather than surfacing an error the operator cannot act
 * on from this screen.
 */
export function useMcfConnexionAlerte() {
  const api = useBillingApi();
  return useQuery({
    queryKey: mcfKeys.alerteConnexion,
    queryFn: () =>
      api.get<McfConnexionAlerteResponse>('/mcf/alerte-connexion'),
    staleTime: 15 * 60_000,
    refetchInterval: 60 * 60_000,
    retry: false,
  });
}

/**
 * The signer status of the caller's établissement, read only by a user who
 * may read invoices (the route's own permission). A failure stays silent.
 */
export function useMcfSignataire() {
  const api = useBillingApi();
  const { hasPermission } = usePermissions();
  return useQuery({
    queryKey: mcfKeys.signataire,
    queryFn: () => api.get<McfSignataire>('/mcf/signataire'),
    enabled: hasPermission(BILLING_PERMISSION_CODES.INVOICE_READ),
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: false,
  });
}

/** A notification row of an outage (`GET /api/mcf/pannes`). */
export interface McfOutageNotification {
  id: string;
  recipient: string;
  channel: string;
  notifiedAt: string;
  reference: string;
}

/**
 * An outage of a registered MCF (arrêté n°2025-0049, art. 7 c)), as
 * `GET /api/mcf/pannes` lists it. `notifiee` holds once its notification rows
 * satisfy the article.
 */
export interface McfOutage {
  id: string;
  establishmentId: string;
  deviceId: string;
  deviceNim: string;
  deviceRole: string;
  cause: string;
  description: string;
  startedAt: string;
  declaredAt: string;
  /** Null while the outage is open. */
  endedAt: string | null;
  endCause: string | null;
  notifiee: boolean;
  notifications: McfOutageNotification[];
}

/**
 * GET /api/mcf/pannes — the tenant's outages (permission `mcf:declarer-panne`).
 * A failure stays silent: the banner it feeds is a reminder, not a gate.
 */
export function useMcfOutages(options: { enabled: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: mcfKeys.outages,
    queryFn: async () => (await api.get<{ pannes: McfOutage[] }>('/mcf/pannes')).pannes,
    enabled: options.enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}
